import React, { useState, useEffect, useCallback } from 'react';
import { axios } from '../../context/AuthContext';
import toast from 'react-hot-toast';

const STATUS_LABELS = {
  400: 'Geçersiz istek (400)',
  403: 'Bu işlem için yetkiniz yok (403)',
  404: 'İstenen kayıt bulunamadı (404)',
  500: 'Sunucu hatası oluştu (500)',
};

function extractApiMessages(err, fallback) {
  const status = err?.response?.status;
  const data = err?.response?.data || {};
  const messages = [];

  if (Array.isArray(data?.errors)) {
    data.errors.forEach((item) => {
      if (typeof item === 'string' && item.trim()) {
        messages.push(item.trim());
        return;
      }
      if (item?.msg) {
        messages.push(item.msg);
      }
    });
  }

  if (!messages.length && data?.errors && typeof data.errors === 'object') {
    Object.values(data.errors).forEach((val) => {
      if (Array.isArray(val)) {
        val.forEach((v) => {
          if (v) messages.push(String(v));
        });
      } else if (val) {
        messages.push(String(val));
      }
    });
  }

  const rootMessage = data?.message || data?.error;
  if (rootMessage) {
    messages.unshift(rootMessage);
  }

  if (!messages.length) {
    messages.push(fallback);
  }

  const prefix = STATUS_LABELS[status];
  if (prefix) {
    return messages.map((m) => `${prefix}: ${m}`);
  }
  return messages;
}

function showApiError(err, fallback) {
  const messages = extractApiMessages(err, fallback);
  messages.forEach((msg, i) => {
    setTimeout(() => toast.error(msg), i * 120);
  });
}

export default function OdevPage() {
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' ? window.innerWidth <= 900 : false);
  const [odevler, setOdevler] = useState([]);
  const [siniflar, setSiniflar] = useState([]);
  const [dersler, setDersler] = useState([]);
  const [aktifOdev, setAktifOdev] = useState(null);
  const [teslimListe, setTeslimListe] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [filters, setFilters] = useState({ sinif_id: '', ders_id: '' });
  const [form, setForm] = useState({ sinif_id: '', ders_id: '', baslik: '', konu: '', soru_sayisi: '', aciklama: '', teslim_tarihi: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.sinif_id) params.sinif_id = filters.sinif_id;
      if (filters.ders_id) params.ders_id = filters.ders_id;

      const [oRes, sinifRes, dersRes] = await Promise.all([
        axios.get('/odevler', { params }),
        axios.get('/siniflar'),
        axios.get('/dersler'),
      ]);

      if (oRes.data.success) setOdevler(oRes.data.data || []);
      if (sinifRes.data.success) setSiniflar(sinifRes.data.data || []);
      if (dersRes.data.success) setDersler(dersRes.data.data || []);
    } catch (err) {
      showApiError(err, 'Ödev verileri yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, [filters.sinif_id, filters.ders_id]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 900);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const loadTeslimListe = async (odev) => {
    try {
      const { data } = await axios.get(`/odevler/${odev.id}/teslim`);
      if (data.success) {
        setAktifOdev(odev);
        setTeslimListe(data.data || []);
      }
    } catch (err) {
      showApiError(err, 'Ödev teslim listesi yüklenemedi');
    }
  };

  const handleCreate = async () => {
    if (!form.sinif_id || !form.ders_id || !form.baslik.trim() || !form.konu.trim() || !form.aciklama.trim() || !form.teslim_tarihi) {
      toast.error('Sınıf, ders, başlık, konu, ödev içeriği ve teslim tarihi zorunlu');
      return;
    }
    if (!Number.isInteger(Number(form.soru_sayisi)) || Number(form.soru_sayisi) <= 0) {
      toast.error('Soru sayısı 0’dan büyük bir tam sayı olmalıdır');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        sinif_id: form.sinif_id,
        ders_id: form.ders_id,
        baslik: form.baslik,
        konu: form.konu,
        soru_sayisi: Number(form.soru_sayisi),
        aciklama: form.aciklama,
        teslim_tarihi: form.teslim_tarihi,
      };
      await axios.post('/odevler', payload);
      toast.success('Ödev oluşturuldu');
      setShowModal(false);
      setForm({ sinif_id: '', ders_id: '', baslik: '', konu: '', soru_sayisi: '', aciklama: '', teslim_tarihi: '' });
      load();
    } catch (err) {
      showApiError(err, 'Ödev oluşturulamadı');
    } finally {
      setSaving(false);
    }
  };

  const toggleTeslim = (ogrenciId, mevcutDurum) => {
    const yeniDurum =
      mevcutDurum === 'teslim_edilmedi'
        ? 'teslim_edildi'
        : mevcutDurum === 'teslim_edildi'
          ? 'gec_teslim'
          : 'teslim_edilmedi';

    setTeslimListe((prev) => prev.map((o) => (o.ogrenci_id === ogrenciId ? { ...o, durum: yeniDurum } : o)));

    axios
      .put(`/odevler/${aktifOdev.id}/teslim`, {
        ogrenci_id: ogrenciId,
        durum: yeniDurum,
      })
      .catch((err) => showApiError(err, 'Teslim durumu güncellenemedi'));
  };

  const DURUM = {
    teslim_edildi: { emoji: '✅', label: 'Yaptı', bg: '#D1FAE5', color: '#065F46', border: '#10B981' },
    teslim_edilmedi: { emoji: '❌', label: 'Yapmadı', bg: '#FEE2E2', color: '#991B1B', border: '#EF4444' },
    gec_teslim: { emoji: '⏰', label: 'Geç Teslim', bg: '#FEF3C7', color: '#92400E', border: '#F59E0B' },
  };

  const teslimSayisi = teslimListe.filter((o) => o.durum === 'teslim_edildi').length;
  const gecTeslimSayisi = teslimListe.filter((o) => o.durum === 'gec_teslim').length;
  const yapmayanSayisi = teslimListe.length - teslimSayisi - gecTeslimSayisi;

  if (aktifOdev) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button className="btn btn-outline btn-sm" onClick={() => setAktifOdev(null)}>
            ← Geri
          </button>
          <div>
            <h2 style={{ fontFamily: 'var(--font-ui)', fontWeight: 900 }}>📝 {aktifOdev.baslik}</h2>
            <p style={{ color: 'var(--text-muted)', fontSize: '0.82rem' }}>
              {aktifOdev.konu || '-'} · {aktifOdev.soru_sayisi || '-'} soru · Teslim: {new Date(aktifOdev.teslim_tarihi).toLocaleDateString('tr-TR')}
            </p>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12 }}>
          {[
            { label: 'Yaptı', count: teslimSayisi, color: '#10B981', bg: '#D1FAE5', emoji: '✅' },
            { label: 'Yapmadı', count: yapmayanSayisi, color: '#EF4444', bg: '#FEE2E2', emoji: '❌' },
            { label: 'Geç Teslim', count: gecTeslimSayisi, color: '#F59E0B', bg: '#FEF3C7', emoji: '⏰' },
          ].map((s) => (
            <div key={s.label} className="card" style={{ background: s.bg, border: `1.5px solid ${s.color}30`, textAlign: 'center', padding: 14 }}>
              <div style={{ fontSize: '1.6rem' }}>{s.emoji}</div>
              <div style={{ fontFamily: 'var(--font-ui)', fontWeight: 900, fontSize: '1.6rem', color: s.color, lineHeight: 1 }}>{s.count}</div>
              <div style={{ fontSize: '0.72rem', color: s.color, fontWeight: 600 }}>{s.label}</div>
            </div>
          ))}
        </div>

        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <div style={{ fontFamily: 'var(--font-ui)', fontWeight: 800 }}>👥 Öğrenci Listesi</div>
            <button
              className="btn btn-sm btn-outline"
              onClick={() => {
                setTeslimListe((prev) => prev.map((o) => ({ ...o, durum: 'teslim_edildi' })));
                teslimListe
                  .filter((o) => o.durum !== 'teslim_edildi')
                  .forEach((o) => toggleTeslim(o.ogrenci_id, o.durum));
              }}
              style={{ fontSize: '0.75rem' }}
            >
              Tümü Yaptı
            </button>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {teslimListe.map((ogr, i) => {
              const d = DURUM[ogr.durum] || DURUM.teslim_edilmedi;
              return (
                <div
                  key={i}
                  onClick={() => toggleTeslim(ogr.ogrenci_id, ogr.durum)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '11px 14px',
                    background: d.bg,
                    border: `1.5px solid ${d.border}25`,
                    borderRadius: 'var(--r-md)',
                    cursor: 'pointer',
                    transition: 'all 0.12s',
                  }}
                >
                  <div className="avatar avatar-sm" style={{ background: `${d.border}25`, color: d.color, fontFamily: 'var(--font-ui)', fontWeight: 800, flexShrink: 0 }}>
                    {ogr.ad?.[0] || ''}
                    {ogr.soyad?.[0] || ''}
                  </div>
                  <div style={{ flex: 1, fontWeight: 700, fontSize: '0.9rem' }}>
                    {ogr.ad} {ogr.soyad}
                  </div>
                  <div style={{ fontSize: '1.3rem' }}>{d.emoji}</div>
                  <div style={{ padding: '3px 10px', borderRadius: 99, fontSize: '0.72rem', fontWeight: 700, background: `${d.border}20`, color: d.color }}>{d.label}</div>
                </div>
              );
            })}
          </div>
        </div>

        <button
          className="btn btn-primary btn-lg"
          style={{ justifyContent: 'center' }}
          onClick={() => {
            toast.success('Ödev kontrolü tamamlandı');
            setAktifOdev(null);
            load();
          }}
        >
          💾 Kontrolü Bitir & Kapat
        </button>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div
        style={{ display: 'flex', justifyContent: 'space-between', alignItems: isMobile ? 'stretch' : 'center', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 10 : 0 }}
        className="animate-fadeInUp"
      >
        <div>
          <h2 style={{ fontFamily: 'var(--font-ui)', fontWeight: 900, fontSize: '1.4rem' }}>📝 Ödev Yönetimi</h2>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>{odevler.length} ödev tanımlı</p>
        </div>
        <button className="btn btn-primary" style={{ width: isMobile ? '100%' : 'auto', justifyContent: 'center' }} onClick={() => setShowModal(true)}>
          + Yeni Ödev
        </button>
      </div>

      <div className="card animate-fadeInUp" style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr auto', gap: 10, alignItems: 'end' }}>
        <div className="form-group">
          <label className="form-label">Sınıf</label>
          <select className="form-input" value={filters.sinif_id} onChange={(e) => setFilters((prev) => ({ ...prev, sinif_id: e.target.value }))}>
            <option value="">Tümü</option>
            {siniflar.map((s) => (
              <option key={s.id} value={s.id}>
                {s.ad}
              </option>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label className="form-label">Ders</label>
          <select className="form-input" value={filters.ders_id} onChange={(e) => setFilters((prev) => ({ ...prev, ders_id: e.target.value }))}>
            <option value="">Tümü</option>
            {dersler.map((d) => (
              <option key={d.id} value={d.id}>
                {d.ad}
              </option>
            ))}
          </select>
        </div>
        <button className="btn btn-outline" style={{ width: isMobile ? '100%' : 'auto', justifyContent: 'center' }} onClick={() => setFilters({ sinif_id: '', ders_id: '' })}>
          Temizle
        </button>
      </div>

      {loading ? (
        <div className="page-loader">
          <div className="spinner" />
        </div>
      ) : odevler.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: 48 }}>
          <div style={{ fontSize: '3rem', marginBottom: 12 }}>📝</div>
          <h3 style={{ fontFamily: 'var(--font-ui)', color: 'var(--text-muted)' }}>Ödev bulunamadı</h3>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>Bu filtrelerle eşleşen ödev yok.</p>
          <button className="btn btn-primary" style={{ marginTop: 16 }} onClick={() => setShowModal(true)}>
            + Yeni Ödev
          </button>
        </div>
      ) : isMobile ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }} className="animate-fadeInUp">
          {odevler.map((o, i) => (
            <div key={i} className="card" style={{ padding: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'flex-start' }}>
                <div style={{ fontWeight: 800 }}>{o.baslik}</div>
                <span className="badge" style={{ background: 'var(--bg-elevated)' }}>
                  {o.teslim_eden || 0}/{o.toplam_ogrenci || 0}
                </span>
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
                <span className="badge badge-info">{o.ders_adi || '-'}</span>
                <span className="badge badge-muted">{o.sinif_adi || '—'}</span>
                <span className="badge badge-muted">{o.soru_sayisi || '-'} soru</span>
              </div>
              {o.konu && <div style={{ marginTop: 8, fontSize: '0.82rem', fontWeight: 700 }}>Konu: {o.konu}</div>}
              {o.aciklama && <div style={{ marginTop: 4, fontSize: '0.78rem', color: 'var(--text-muted)' }}>{o.aciklama}</div>}
              <div style={{ marginTop: 8, fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                Veriliş: {new Date(o.verilis_tarihi || o.created_at).toLocaleDateString('tr-TR')}
              </div>
              <div style={{ fontSize: '0.8rem', fontWeight: 700, color: new Date(o.teslim_tarihi) < new Date() ? 'var(--accent)' : 'var(--text-secondary)' }}>
                Teslim: {new Date(o.teslim_tarihi).toLocaleDateString('tr-TR')}
              </div>
              <button className="btn btn-outline btn-sm" style={{ marginTop: 10, width: '100%', justifyContent: 'center' }} onClick={() => loadTeslimListe(o)}>
                📋 Ödev Kontrol
              </button>
            </div>
          ))}
        </div>
      ) : (
        <div className="table-container animate-fadeInUp">
          <table className="table">
            <thead>
              <tr>
                <th>Ödev</th>
                <th>Sınıf</th>
                <th>Ders</th>
                <th>Veriliş</th>
                <th>Teslim</th>
                <th>Teslim Eden</th>
                <th>İşlem</th>
              </tr>
            </thead>
            <tbody>
              {odevler.map((o, i) => (
                <tr key={i}>
                  <td><div style={{ fontWeight: 700 }}>{o.baslik}</div><div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>{o.konu || '—'} · {o.soru_sayisi || '-'} soru</div></td>
                  <td>{o.sinif_adi || '—'}</td>
                  <td>
                    <span className="badge" style={{ background: 'var(--blue-dim)', color: 'var(--blue)' }}>
                      {o.ders_adi || '-'}
                    </span>
                  </td>
                  <td style={{ fontSize: '0.82rem' }}>{new Date(o.verilis_tarihi || o.created_at).toLocaleDateString('tr-TR')}</td>
                  <td style={{ fontSize: '0.82rem', fontWeight: 600, color: new Date(o.teslim_tarihi) < new Date() ? 'var(--accent)' : 'var(--text-secondary)' }}>
                    {new Date(o.teslim_tarihi).toLocaleDateString('tr-TR')}
                  </td>
                  <td>
                    <span className="badge" style={{ background: 'var(--bg-elevated)' }}>
                      {o.teslim_eden || 0}/{o.toplam_ogrenci || 0}
                    </span>
                  </td>
                  <td>
                    <button className="btn btn-outline btn-sm" onClick={() => loadTeslimListe(o)}>
                      📋 Ödev Kontrol
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div className="modal-backdrop" onClick={() => setShowModal(false)}>
          <div className="modal" style={{ maxWidth: 540 }} onClick={(e) => e.stopPropagation()}>
            <h3 className="modal-title">📝 Yeni Ödev Oluştur</h3>
            <p className="modal-subtitle">Kurumdaki herhangi bir sınıfa ödev atayın</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 12 }}>
                <div className="form-group">
                  <label className="form-label">Sınıf *</label>
                  <select className="form-input" value={form.sinif_id} onChange={(e) => setForm({ ...form, sinif_id: e.target.value })}>
                    <option value="">Seçin</option>
                    {siniflar.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.ad}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Ders *</label>
                  <select className="form-input" value={form.ders_id} onChange={(e) => setForm({ ...form, ders_id: e.target.value })}>
                    <option value="">Seçin</option>
                    {dersler.map((d) => (
                      <option key={d.id} value={d.id}>
                        {d.ad}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Ödev Başlığı *</label>
                <input className="form-input" placeholder="Türev Soruları 1-30" value={form.baslik} onChange={(e) => setForm({ ...form, baslik: e.target.value })} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '2fr 1fr', gap: 12 }}>
                <div className="form-group">
                  <label className="form-label">Konu *</label>
                  <input className="form-input" placeholder="Örn. Türevde zincir kuralı" value={form.konu} onChange={(e) => setForm({ ...form, konu: e.target.value })} />
                </div>
                <div className="form-group">
                  <label className="form-label">Soru Sayısı *</label>
                  <input className="form-input" type="number" min="1" step="1" placeholder="30" value={form.soru_sayisi} onChange={(e) => setForm({ ...form, soru_sayisi: e.target.value })} />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Ödev İçeriği / Açıklama *</label>
                <textarea
                  className="form-input"
                  style={{ minHeight: 80 }}
                  placeholder="Çözülecek kaynak, sayfa aralığı ve diğer detaylar..."
                  value={form.aciklama}
                  onChange={(e) => setForm({ ...form, aciklama: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Teslim Tarihi *</label>
                <input className="form-input" type="date" value={form.teslim_tarihi} onChange={(e) => setForm({ ...form, teslim_tarihi: e.target.value })} />
              </div>
              <div style={{ display: 'flex', gap: 10, marginTop: 6, flexDirection: isMobile ? 'column' : 'row' }}>
                <button className="btn btn-primary" style={{ flex: 1, justifyContent: 'center' }} onClick={handleCreate} disabled={saving}>
                  {saving ? (
                    <>
                      <div className="spinner" style={{ width: 16, height: 16 }} /> Oluşturuluyor...
                    </>
                  ) : (
                    '📤 Ödev Oluştur & Gönder'
                  )}
                </button>
                <button className="btn btn-outline" style={{ width: isMobile ? '100%' : 'auto', justifyContent: 'center' }} onClick={() => setShowModal(false)}>
                  İptal
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
